/**
 * Explicit RFID tag formats for vehicle tags.
 *
 * The same digits name different physical cards depending on which screen they came from, and
 * the string itself cannot say which — "01019872" is Wiegand 010,19872 on one system and padded
 * hex on another, two different cards. Guessing silently writes the wrong one, and the only
 * symptom is a resident stopped at a gate while every screen looks correct.
 *
 * So the format is recorded alongside the tag. `auto` reproduces the historical guess exactly,
 * which is what every pre-existing row carries, so nothing already stored changes meaning.
 */

export type RfidTagFormat = 'auto' | 'hex' | 'dec' | 'wg' | 'id';

export const RFID_TAG_FORMATS: RfidTagFormat[] = ['auto', 'hex', 'dec', 'wg', 'id'];

export const RFID_TAG_FORMAT_LABELS: Record<RfidTagFormat, string> = {
  auto: 'Auto',
  hex: 'HEX',
  dec: 'DEC',
  wg: 'WG (Wiegand)',
  id: 'ID (Control ID)',
};

export const RFID_TAG_FORMAT_HINTS: Record<RfidTagFormat, string> = {
  auto: 'Detecta o formato pelo valor (0x ou letras A-F = HEX, zero à esquerda = HEX, vírgula = Wiegand). Use quando não souber a origem do número.',
  hex: 'Número do cartão em hexadecimal. Ex: 4BAC5 ou 04BAC5',
  dec: 'Número do cartão em decimal. Ex: 309957',
  wg: 'Wiegand "facilidade,cartão". Ex: 4,47813 — ou os 8 dígitos sem vírgula: 00447813',
  id: 'Valor já codificado pela ControlID (o campo ID na antena). Gravado exatamente como está. Ex: 17179916997',
};

/** ControlID packs a card as fc<<32 | cn. Beyond 2^32, so never with JS bitwise operators. */
const CONTROLID_FC_SHIFT = 2 ** 32;

/** Wiegand-26 field widths: 8 bits of facility code, 16 bits of card number. */
const MAX_FACILITY_CODE = 255;
const MAX_CARD_NUMBER = 65535;

/** A card number (hex/dec) splits 16/16 before being packed into the ControlID form. */
function packCardNumber(cardNumber: number): number | null {
  if (!Number.isFinite(cardNumber) || cardNumber <= 0) return null;
  const fc = Math.floor(cardNumber / 65536) & 0xffff;
  const cn = cardNumber % 65536;
  return fc * CONTROLID_FC_SHIFT + cn;
}

function parseDigits(raw: string, radix: 10 | 16): number | null {
  const t = raw.trim();
  if (!t) return null;

  if (radix === 10) {
    // Explicitly decimal: letters mean the operator picked the wrong format, and reading them
    // as hex anyway is the silent-wrong-card behaviour this whole feature exists to remove.
    if (!/^\d+$/.test(t)) return null;
    const v = parseInt(t, 10);
    return Number.isFinite(v) && v > 0 ? v : null;
  }

  const hex = /^0[xX]/.test(t) ? t.slice(2) : t;
  if (!/^[0-9a-fA-F]+$/.test(hex)) return null;
  const v = parseInt(hex, 16);
  return Number.isFinite(v) && v > 0 ? v : null;
}

/** "4,47813" or the 8-digit padded "00447813" (FFF CCCCC). */
function parseWiegand(raw: string): { fc: number; cn: number } | null {
  const t = raw.trim();
  if (!t) return null;

  let fc: number;
  let cn: number;

  if (t.includes(',')) {
    const parts = t.split(',');
    if (parts.length !== 2) return null;
    fc = parseInt(parts[0].trim(), 10);
    cn = parseInt(parts[1].trim(), 10);
  } else if (/^\d{8}$/.test(t)) {
    // Other systems print Wiegand as 8 digits with the separator dropped. Splitting 3+5 is not
    // a convention but the field widths: fc maxes at 255 (3 digits), cn at 65535 (5 digits).
    fc = parseInt(t.slice(0, 3), 10);
    cn = parseInt(t.slice(3), 10);
  } else {
    return null;
  }

  if (!Number.isFinite(fc) || !Number.isFinite(cn)) return null;
  // Out-of-range means this is not really Wiegand-26 — see the guaritaSerial writeup, where a
  // 40-bit UHF value reused as "Wiegand" produced a card the hardware always rejected.
  if (fc < 0 || fc > MAX_FACILITY_CODE) return null;
  if (cn < 0 || cn > MAX_CARD_NUMBER) return null;
  if (fc === 0 && cn === 0) return null;

  return { fc, cn };
}

/**
 * The legacy guess, preserved byte for byte.
 *
 * Every tag stored before this feature carries format `auto`, so this path must keep resolving
 * them to exactly the card they resolve to today. It is deliberately a copy of
 * deviceCommunication.parseRfidTagToCardValue's rules rather than an improvement on them.
 */
function autoCardValue(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;

  if (t.includes(',')) {
    const wg = parseWiegand(t);
    return wg ? wg.fc * CONTROLID_FC_SHIFT + wg.cn : null;
  }

  const has0x = /^0[xX]/.test(t);
  const hasHexLetters = /[a-fA-F]/.test(t);
  const hasLeadingZero = t.length > 1 && t.startsWith('0') && !has0x;

  const value = has0x || hasHexLetters || hasLeadingZero
    ? parseDigits(t, 16)
    : parseDigits(t, 10);

  return value === null ? null : packCardNumber(value);
}

/**
 * The value to write to a ControlID device for this tag.
 * Returns null when the tag cannot be read in the stated format — callers must treat that as
 * "do not sync", never as zero.
 */
export function toCardValue(tag: string | null | undefined, format: RfidTagFormat = 'auto'): number | null {
  const t = (tag ?? '').trim();
  if (!t) return null;

  switch (format) {
    case 'id': {
      // Already the final card value. Re-encoding it yields a different card — and with JS
      // 32-bit bitwise operators the facility code was silently dropped entirely.
      const v = parseDigits(t, 10);
      return v === null ? null : v;
    }
    case 'wg': {
      const wg = parseWiegand(t);
      return wg ? wg.fc * CONTROLID_FC_SHIFT + wg.cn : null;
    }
    case 'hex': {
      const v = parseDigits(t, 16);
      return v === null ? null : packCardNumber(v);
    }
    case 'dec': {
      const v = parseDigits(t, 10);
      return v === null ? null : packCardNumber(v);
    }
    case 'auto':
    default:
      return autoCardValue(t);
  }
}

export interface TagDescription {
  /** Card number in hex, no padding. */
  hex: string;
  /** Card number in decimal. */
  dec: number;
  /** Wiegand "FFF,CCCCC". */
  wg: string;
  /** The ControlID-encoded value actually written to the device. */
  id: number;
}

/**
 * All four views of one card, derived from the single resolved value so they cannot disagree.
 */
export function describeTag(tag: string | null | undefined, format: RfidTagFormat = 'auto'): TagDescription | null {
  const card = toCardValue(tag, format);
  if (card === null) return null;

  const fc = Math.floor(card / CONTROLID_FC_SHIFT);
  const cn = card % CONTROLID_FC_SHIFT;
  const cardNumber = fc * 65536 + cn;

  return {
    hex: cardNumber.toString(16).toUpperCase(),
    dec: cardNumber,
    wg: `${String(fc).padStart(3, '0')},${String(cn).padStart(5, '0')}`,
    id: card,
  };
}
